"use client";

// NettiUiBridge — performs what netti asks the SCREEN to do.
//
// A tool never touches the page. It returns a directive (lib/netti/tools/types
// NettiUi) and the chat hook broadcasts it on the window as
// `innernet:netti-ui` ({ detail: NettiUi }). This one component, mounted once
// inside the dock, hears it and does the thing the person can see:
//
//   open_connect  → the guided connect window for one tool, over the page, the
//                   same ConnectGuideModal the connections list opens. It reads
//                   the person's own connections (the tool may have just minted
//                   one) and, when asked for a phase other than the first, walks
//                   to that chip once the window is up.
//   panel_view    → a panel of the dashboard on one of its tabs. The view is left
//                   in sessionStorage for a panel that has not mounted yet and
//                   announced on `innernet:panel-view` for one that has; then the
//                   shell is carried there.
//   open_import   → the import window (the one settings → data uses).
//   open_tab      → a public page (docs, community, company…) in a NEW tab, so the
//                   dashboard the person is in never goes away.
//   open_web      → a page on the open web (a link row of kind web), in a NEW browser
//                   tab with no opener. https only, no credentials in the address.
//
// Every path is checked here too: only an in-site path ever opens as a site page,
// never a protocol-relative or backslash one, and never an api route. The open web
// has its own door, and it is https alone: the shell re-sends a web row's address
// as open_tab, so an https address that arrives there is handed to that same door.

import * as React from "react";
import ConnectGuideModal from "../connect/ConnectGuideModal";
import ConnectGuideModalV1 from "../connect/ConnectGuideModal.v1";
import { useNext } from "../../lib/release/next";
import ImportSteps from "../connect/ImportSteps";
import { CONNECT_TOOLS, type ConnectTool } from "../../lib/connect/tools";
import type { Connection } from "../../lib/connect/connections";
import type { NettiUi } from "../../lib/netti/tools/types";
import { PANEL_VIEW_IDS, safeWebUrl } from "../../lib/netti/linkActions";

export const NETTI_UI_EVENT = "innernet:netti-ui";
export const PANEL_VIEW_EVENT = "innernet:panel-view";
export const panelViewKey = (panel: string) => `innernet:panel-view:${panel}`;

/** an in-site path: starts with one slash, never `//` or `/\` (an off-site url in disguise) */
const SAFE_PATH = /^\/(?![\/\\])/;

/** the panels that take a view, and the views they really have (one table, lib/netti/linkActions) */
const PANEL_VIEWS = PANEL_VIEW_IDS;
const PANELS = new Set(["settings", "connections", "agents", "netti", "you", "memory"]);

/** the chip a phase lives under in the connect window (its own labels) */
const PHASE_CHIP: Record<string, string> = { permissions: "permissions", instructions: "instructions", import: "import" };

interface ConnectRequest {
  tool: ConnectTool;
  phase?: string;
  connections: Connection[];
  nonce: number;
}

async function readConnections(): Promise<Connection[]> {
  try {
    const res = await fetch("/api/connections", { cache: "no-store" });
    if (!res.ok) return [];
    const data = (await res.json()) as { connections?: Connection[] };
    return data.connections ?? [];
  } catch {
    return []; // offline — the window still opens, on its naming step
  }
}

export default function NettiUiBridge(): React.JSX.Element | null {
  const [connect, setConnect] = React.useState<ConnectRequest | null>(null);
  // the v1.1 connect window is a test account's (lib/release/next)
  const ConnectWindow = useNext() ? ConnectGuideModal : ConnectGuideModalV1;
  const [importing, setImporting] = React.useState(false);
  const nonce = React.useRef(0);

  const refresh = React.useCallback(async () => {
    const rows = await readConnections();
    setConnect((c) => (c ? { ...c, connections: rows } : c));
  }, []);
  // stable handles: the connect window restarts its watch when these change
  const onChanged = React.useCallback(() => { void refresh(); }, [refresh]);
  const onCloseConnect = React.useCallback(() => setConnect(null), []);
  const onCloseImport = React.useCallback(() => setImporting(false), []);

  React.useEffect(() => {
    let alive = true;

    const perform = async (ui: NettiUi) => {
      // 1 — a panel on one of its tabs
      if (ui.panel_view && PANELS.has(ui.panel_view.panel)) {
        const { panel, view } = ui.panel_view;
        const known = PANEL_VIEWS[panel];
        if (known && known.includes(view)) {
          try { sessionStorage.setItem(panelViewKey(panel), view); } catch { /* private mode — the event below still reaches a mounted panel */ }
          window.dispatchEvent(new CustomEvent(PANEL_VIEW_EVENT, { detail: { panel, view } }));
        }
        window.dispatchEvent(new CustomEvent("innernet:netti-nav", { detail: { path: `/${panel}` } }));
      }

      // 2 — the import window (never two sheets at once)
      if (ui.open_import) {
        setConnect(null);
        setImporting(true);
      }

      // 3 — the connect window for one tool
      if (ui.open_connect) {
        const tool = CONNECT_TOOLS.find((t) => t.id === ui.open_connect!.tool);
        if (tool) {
          const rows = await readConnections();
          if (!alive) return;
          setImporting(false);
          setConnect({ tool, phase: ui.open_connect.phase, connections: rows, nonce: ++nonce.current });
        }
      }

      // 4 — a public page, beside the dashboard
      if (typeof ui.open_tab === "string" && SAFE_PATH.test(ui.open_tab) && !ui.open_tab.startsWith("/api/")) {
        window.open(ui.open_tab, "_blank", "noopener");
      }

      // 5 — a page on the open web: its own door, https only, never an opener.
      // A web row's address reaches the shell as a nav path and comes back here as
      // open_tab; an https address there is the same thing, so it goes the same way.
      const web = safeWebUrl(ui.open_web) ?? (typeof ui.open_tab === "string" ? safeWebUrl(ui.open_tab) : null);
      if (web) window.open(web, "_blank", "noopener,noreferrer");
    };

    const onUi = (e: Event) => {
      const ui = (e as CustomEvent<NettiUi | undefined>).detail;
      if (ui && typeof ui === "object") void perform(ui);
    };
    window.addEventListener(NETTI_UI_EVENT, onUi);
    return () => {
      alive = false;
      window.removeEventListener(NETTI_UI_EVENT, onUi);
    };
  }, []);

  // asked for a phase other than the first: once the window is up, walk to that
  // chip (the window's own tab). Only when the person already has a connection
  // for this tool: from the naming step the same press would mint one, and
  // netti never makes a door the person did not ask for.
  React.useEffect(() => {
    const chip = connect?.phase ? PHASE_CHIP[connect.phase] : undefined;
    if (!connect || !chip || connect.connections.length === 0) return;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      const tabs = Array.from(document.querySelectorAll<HTMLElement>('[role="tab"]'));
      const hit = tabs.find((t) => (t.textContent ?? "").trim().toLowerCase() === chip);
      if (hit) {
        clearInterval(timer);
        if (hit.getAttribute("aria-selected") !== "true") hit.click();
      } else if (tries > 20) {
        clearInterval(timer);
      }
    }, 80);
    return () => clearInterval(timer);
  }, [connect]);

  return (
    <>
      {connect && (
        <ConnectWindow
          key={connect.nonce}
          tool={connect.tool}
          connections={connect.connections.filter((c) => c.tool_id === connect.tool.id)}
          accountName={null}
          onClose={onCloseConnect}
          onChanged={onChanged}
        />
      )}
      {importing && <ImportSteps onClose={onCloseImport} openedFrom="netti" />}
    </>
  );
}
